import React, { type CSSProperties } from 'react';
import {
    Award,
    MessageCircle,
    MessageSquareQuote,
    Quote,
    ShieldCheck,
    Sparkles,
    Star,
} from 'lucide-react';
import {
    testimonyQuoteIconTokens,
    testimonyQuoteTokens,
    type TestimonyQuoteColorToken,
    type TestimonyQuoteIconSlot,
    type TestimonyQuoteIconToken,
} from './TestimonyQuote.tokens';

export type TestimonyQuoteImage = {
    src?:
        | string
        | {
              src?: string;
              width?: number;
              height?: number;
              [key: string]: unknown;
          };
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
    objectPosition?: string;
};

export type TestimonyQuoteColors = Partial<Record<TestimonyQuoteColorToken, string>>;

export type TestimonyQuoteIconTokens = Partial<
    Record<TestimonyQuoteIconSlot, TestimonyQuoteIconToken>
>;

export type TestimonyQuoteImagePosition = 'left' | 'right';

export type TestimonyQuoteProps = {
    quote: React.ReactNode;
    eyebrow?: React.ReactNode;
    authorName?: React.ReactNode;
    authorRole?: React.ReactNode;
    authorMeta?: React.ReactNode;
    image?: string | TestimonyQuoteImage | false;
    imagePosition?: TestimonyQuoteImagePosition;
    imageAlt?: string;
    imageObjectPosition?: string;
    imageAspectRatio?: string;
    quoteIcon?: React.ReactNode | false;
    quoteIconToken?: TestimonyQuoteIconToken;
    icons?: TestimonyQuoteIconTokens;
    colors?: TestimonyQuoteColors;
    colours?: TestimonyQuoteColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    textSoftColor?: string;
    textSoftColour?: string;
    textMutedColor?: string;
    textMutedColour?: string;
    maxWidth?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type TestimonyQuoteCssVars = CSSProperties &
    Record<`--testimony-quote-${string}`, string | number | undefined>;

type NormalizedImage = {
    src: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
    loading: 'eager' | 'lazy';
    decoding: 'async' | 'auto' | 'sync';
    decorative: boolean;
    objectPosition: string;
};

const t = testimonyQuoteTokens;

const iconRegistry: Record<TestimonyQuoteIconToken, React.ElementType> = {
    quote: Quote,
    messageQuote: MessageSquareQuote,
    messageCircle: MessageCircle,
    sparkles: Sparkles,
    star: Star,
    award: Award,
    shieldCheck: ShieldCheck,
};

const testimonyQuoteCss = `
.testimony-quote {
  --testimony-quote-bg: ${t.colors.background};
  --testimony-quote-surface: ${t.colors.surface};
  --testimony-quote-text: ${t.colors.text};
  --testimony-quote-text-soft: ${t.colors.textSoft};
  --testimony-quote-text-muted: ${t.colors.textMuted};
  --testimony-quote-accent: ${t.colors.accent};
  --testimony-quote-accent-contrast: ${t.colors.accentContrast};
  --testimony-quote-accent-soft: ${t.colors.accentSoft};
  --testimony-quote-border: ${t.colors.border};
  --testimony-quote-image-fallback: ${t.colors.imageFallback};
  --testimony-quote-shadow: ${t.colors.shadow};
  --testimony-quote-max-width: ${t.sizes.maxWidth}px;
  --testimony-quote-image-ratio: ${t.sizes.imageAspectRatio};
  --testimony-quote-image-position: ${t.image.objectPosition};
  width: 100%;
  overflow: hidden;
  background: var(--testimony-quote-bg);
  color: var(--testimony-quote-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.testimony-quote,
.testimony-quote * {
  box-sizing: border-box;
}
.testimony-quote-shell {
  width: 100%;
  max-width: var(--testimony-quote-max-width);
  margin: 0 auto;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
}
.testimony-quote-card {
  display: grid;
  grid-template-areas: "content media";
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
  align-items: stretch;
  gap: ${t.spacing.cardGap}px;
  width: 100%;
  padding: ${t.spacing.cardPadding}px;
  border: 1px solid var(--testimony-quote-border);
  border-radius: ${t.radius.card}px;
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--testimony-quote-accent) 5%, transparent), transparent 34%),
    var(--testimony-quote-surface);
  box-shadow: var(--testimony-quote-shadow);
}
.testimony-quote-card[data-image="false"] {
  max-width: 940px;
  margin: 0 auto;
  grid-template-areas: "content";
  grid-template-columns: minmax(0, 1fr);
}
.testimony-quote-card[data-image-position="left"] {
  grid-template-areas: "media content";
  grid-template-columns: minmax(280px, 420px) minmax(0, 1fr);
}
.testimony-quote-card[data-image="false"][data-image-position="left"] {
  grid-template-areas: "content";
  grid-template-columns: minmax(0, 1fr);
}
.testimony-quote-content {
  grid-area: content;
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
}
.testimony-quote-eyebrow {
  margin: 0 0 20px;
  color: var(--testimony-quote-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
  overflow-wrap: anywhere;
}
.testimony-quote-icon {
  display: inline-flex;
  width: ${t.sizes.icon}px;
  height: ${t.sizes.icon}px;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.icon}px;
  background: var(--testimony-quote-accent-soft);
  color: var(--testimony-quote-accent);
}
.testimony-quote-icon svg {
  width: 27px;
  height: 27px;
}
.testimony-quote-blockquote {
  margin: 24px 0 0;
  color: var(--testimony-quote-text);
  font-size: ${t.typography.quote.fontSize}px;
  line-height: ${t.typography.quote.lineHeight};
  font-weight: ${t.typography.quote.fontWeight};
  letter-spacing: ${t.typography.quote.letterSpacing};
  overflow-wrap: anywhere;
}
.testimony-quote-blockquote > :first-child {
  margin-top: 0;
}
.testimony-quote-blockquote > :last-child {
  margin-bottom: 0;
}
.testimony-quote-figcaption {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 32px;
  color: var(--testimony-quote-text-soft);
}
.testimony-quote-rule {
  flex: 0 0 36px;
  width: 36px;
  height: 3px;
  margin-top: 9px;
  border-radius: ${t.radius.icon}px;
  background: var(--testimony-quote-accent);
}
.testimony-quote-author {
  min-width: 0;
}
.testimony-quote-name {
  margin: 0;
  color: var(--testimony-quote-text);
  font-size: ${t.typography.author.fontSize}px;
  line-height: ${t.typography.author.lineHeight};
  font-weight: ${t.typography.author.fontWeight};
  letter-spacing: ${t.typography.author.letterSpacing};
  overflow-wrap: anywhere;
}
.testimony-quote-meta {
  margin: 7px 0 0;
  color: var(--testimony-quote-text-muted);
  font-size: ${t.typography.meta.fontSize}px;
  line-height: ${t.typography.meta.lineHeight};
  font-weight: ${t.typography.meta.fontWeight};
  letter-spacing: ${t.typography.meta.letterSpacing};
  overflow-wrap: anywhere;
}
.testimony-quote-media {
  grid-area: media;
  min-width: 0;
  min-height: ${t.sizes.imageMinHeight}px;
  overflow: hidden;
  border-radius: ${t.radius.image}px;
  background: var(--testimony-quote-image-fallback);
  aspect-ratio: var(--testimony-quote-image-ratio);
}
.testimony-quote-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--testimony-quote-image-position);
}
@container (max-width: 980px) {
  .testimony-quote-card,
  .testimony-quote-card[data-image-position="left"] {
    gap: ${t.spacing.tabletCardGap}px;
    padding: ${t.spacing.tabletCardPadding}px;
    grid-template-columns: minmax(0, 1fr) minmax(240px, 340px);
  }
  .testimony-quote-card[data-image-position="left"] {
    grid-template-columns: minmax(240px, 340px) minmax(0, 1fr);
  }
  .testimony-quote-blockquote {
    font-size: ${t.typography.quote.tabletFontSize}px;
  }
  .testimony-quote-media {
    min-height: 340px;
  }
}
@container (max-width: 720px) {
  .testimony-quote-shell {
    padding: ${t.spacing.mobileSectionY}px ${t.spacing.mobileSectionX}px;
  }
  .testimony-quote-card,
  .testimony-quote-card[data-image-position="left"],
  .testimony-quote-card[data-image="false"],
  .testimony-quote-card[data-image="false"][data-image-position="left"] {
    grid-template-areas: "content" "media";
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    padding: ${t.spacing.mobileCardPadding}px;
  }
  .testimony-quote-card[data-image-position="left"] {
    grid-template-areas: "media" "content";
  }
  .testimony-quote-card[data-image="false"],
  .testimony-quote-card[data-image="false"][data-image-position="left"] {
    grid-template-areas: "content";
  }
  .testimony-quote-eyebrow {
    margin-bottom: 16px;
  }
  .testimony-quote-icon {
    width: 48px;
    height: 48px;
  }
  .testimony-quote-icon svg {
    width: 23px;
    height: 23px;
  }
  .testimony-quote-blockquote {
    margin-top: 20px;
    font-size: ${t.typography.quote.mobileFontSize}px;
    line-height: ${t.typography.quote.mobileLineHeight};
  }
  .testimony-quote-figcaption {
    margin-top: 26px;
  }
  .testimony-quote-name {
    font-size: ${t.typography.author.mobileFontSize}px;
  }
  .testimony-quote-meta {
    font-size: ${t.typography.meta.mobileFontSize}px;
  }
  .testimony-quote-media {
    min-height: ${t.sizes.mobileImageMinHeight}px;
  }
}
`;

function getCssSize(value: number | string | undefined): string | undefined {
    if (value === undefined) return undefined;

    return typeof value === 'number' ? `${value}px` : value;
}

function getImageSource(image?: string | TestimonyQuoteImage | false): string {
    if (!image) return '';
    if (typeof image === 'string') return image;
    if (typeof image.src === 'string') return image.src;
    if (image.src?.src) return image.src.src;

    return image.url || image.original || image.thumbnail || '';
}

function getImageWidth(image?: string | TestimonyQuoteImage | false): number | undefined {
    if (!image || typeof image === 'string') return undefined;

    return image.width || (typeof image.src === 'object' ? image.src.width : undefined);
}

function getImageHeight(image?: string | TestimonyQuoteImage | false): number | undefined {
    if (!image || typeof image === 'string') return undefined;

    return image.height || (typeof image.src === 'object' ? image.src.height : undefined);
}

function getTextFallback(value?: React.ReactNode): string | undefined {
    return typeof value === 'string' ? value : undefined;
}

function getNormalizedImage({
    image,
    imageAlt,
    imageObjectPosition,
    authorName,
}: {
    image?: string | TestimonyQuoteImage | false;
    imageAlt?: string;
    imageObjectPosition?: string;
    authorName?: React.ReactNode;
}): NormalizedImage | null {
    const src = getImageSource(image);

    if (!src) return null;

    const imageObject = typeof image === 'object' ? image : undefined;
    const decorative = !!imageObject?.decorative;
    const fallbackAlt = getTextFallback(authorName);

    return {
        src,
        alt: decorative
            ? ''
            : imageAlt || imageObject?.alt || imageObject?.title || fallbackAlt || '',
        title: imageObject?.title,
        width: getImageWidth(image),
        height: getImageHeight(image),
        loading: imageObject?.loading || 'lazy',
        decoding: imageObject?.decoding || 'async',
        decorative,
        objectPosition:
            imageObjectPosition || imageObject?.objectPosition || t.image.objectPosition,
    };
}

function TokenIcon({
    token,
    size = 27,
    strokeWidth = 2.25,
}: {
    token: TestimonyQuoteIconToken;
    size?: number;
    strokeWidth?: number;
}) {
    const Icon = iconRegistry[token] || Quote;

    return <Icon aria-hidden="true" focusable="false" size={size} strokeWidth={strokeWidth} />;
}

export default function TestimonyQuote({
    quote,
    eyebrow,
    authorName,
    authorRole,
    authorMeta,
    image,
    imagePosition = 'right',
    imageAlt,
    imageObjectPosition,
    imageAspectRatio = t.sizes.imageAspectRatio,
    quoteIcon,
    quoteIconToken,
    icons,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    textSoftColor,
    textSoftColour,
    textMutedColor,
    textMutedColour,
    maxWidth = t.sizes.maxWidth,
    id,
    className = '',
    style,
    ariaLabel,
}: TestimonyQuoteProps) {
    if (!quote) return null;

    const resolvedImage = getNormalizedImage({
        image,
        imageAlt,
        imageObjectPosition,
        authorName,
    });
    const resolvedIcons: Required<TestimonyQuoteIconTokens> = {
        ...t.icons,
        ...icons,
    };
    const mergedColors = {
        ...t.colors,
        ...colours,
        ...colors,
    };
    const resolvedAccent = accentColor || accentColour || mergedColors.accent;
    const sectionStyle: TestimonyQuoteCssVars = {
        '--testimony-quote-bg': backgroundColor || backgroundColour || mergedColors.background,
        '--testimony-quote-surface': surfaceColor || surfaceColour || mergedColors.surface,
        '--testimony-quote-text': textColor || textColour || mergedColors.text,
        '--testimony-quote-text-soft': textSoftColor || textSoftColour || mergedColors.textSoft,
        '--testimony-quote-text-muted': textMutedColor || textMutedColour || mergedColors.textMuted,
        '--testimony-quote-accent': resolvedAccent,
        '--testimony-quote-accent-contrast': mergedColors.accentContrast,
        '--testimony-quote-accent-soft': mergedColors.accentSoft,
        '--testimony-quote-border': mergedColors.border,
        '--testimony-quote-image-fallback': mergedColors.imageFallback,
        '--testimony-quote-shadow': mergedColors.shadow,
        '--testimony-quote-max-width': getCssSize(maxWidth),
        '--testimony-quote-image-ratio': imageAspectRatio,
        '--testimony-quote-image-position': resolvedImage?.objectPosition || imageObjectPosition,
        ...style,
    };
    const resolvedQuoteIconToken = quoteIconToken || resolvedIcons.quote;
    const showFigcaption = !!(authorName || authorRole || authorMeta);
    const resolvedAuthorMeta = authorMeta || authorRole;
    const accessibleLabel =
        ariaLabel || getTextFallback(authorName) || getTextFallback(eyebrow) || 'Testimonial quote';

    return (
        <section
            id={id}
            className={`testimony-quote ${className}`.trim()}
            data-component="testimony-quote"
            aria-label={accessibleLabel}
            style={sectionStyle}
        >
            <style>{testimonyQuoteCss}</style>
            <div className="testimony-quote-shell">
                <figure
                    className="testimony-quote-card"
                    data-image={resolvedImage ? 'true' : 'false'}
                    data-image-position={imagePosition}
                >
                    <div className="testimony-quote-content">
                        {eyebrow ? <p className="testimony-quote-eyebrow">{eyebrow}</p> : null}

                        {quoteIcon !== false ? (
                            <span className="testimony-quote-icon" aria-hidden="true">
                                {quoteIcon || <TokenIcon token={resolvedQuoteIconToken} />}
                            </span>
                        ) : null}

                        <blockquote className="testimony-quote-blockquote">
                            {typeof quote === 'string' ? <p>{quote}</p> : quote}
                        </blockquote>

                        {showFigcaption ? (
                            <figcaption className="testimony-quote-figcaption">
                                <span className="testimony-quote-rule" aria-hidden="true" />
                                <div className="testimony-quote-author">
                                    {authorName ? (
                                        <p className="testimony-quote-name">{authorName}</p>
                                    ) : null}
                                    {resolvedAuthorMeta ? (
                                        <p className="testimony-quote-meta">{resolvedAuthorMeta}</p>
                                    ) : null}
                                </div>
                            </figcaption>
                        ) : null}
                    </div>

                    {resolvedImage ? (
                        <div className="testimony-quote-media">
                            <img
                                className="testimony-quote-image"
                                src={resolvedImage.src}
                                alt={resolvedImage.alt}
                                title={resolvedImage.title}
                                width={resolvedImage.width}
                                height={resolvedImage.height}
                                loading={resolvedImage.loading}
                                decoding={resolvedImage.decoding}
                            />
                        </div>
                    ) : null}
                </figure>
            </div>
        </section>
    );
}

export { testimonyQuoteIconTokens, testimonyQuoteTokens };
